import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { normalizeCountryCode } from '../../../shared/components/CountryFlag';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import worldJson from '../../../shared/components/world.json';
import { useTheme } from '../../../theme/ThemeContext';
import { useTranslation } from '../../../shared/i18n';

if (!echarts.getMap('world')) {
    echarts.registerMap('world', worldJson);
}

/** Chart tokens per theme (mirror theme/themeConfig.js cssVariables). */
export const useChartTokens = () => {
    const { isDark } = useTheme();
    return useMemo(() => (isDark ? {
        brand: '#4d7bff', brandSoft: 'rgba(77,123,255,0.22)', text2: '#a9b4cf', text3: '#7c88a8',
        line: '#22305e', land: '#18234a', avail: '#2b448f', surface: '#0d1636', tipBg: '#0d1636', tipText: '#e7ecf7',
    } : {
        brand: '#2357ff', brandSoft: 'rgba(35,87,255,0.12)', text2: '#4a5672', text3: '#8590a6',
        line: '#dfe5ef', land: '#e9edf5', avail: '#b9c9ff', surface: '#ffffff', tipBg: '#ffffff', tipText: '#0b1530',
    }), [isDark]);
};

const tooltipBase = (t) => ({
    backgroundColor: t.tipBg, borderColor: t.line, textStyle: { color: t.tipText, fontSize: 12 },
    extraCssText: 'box-shadow:0 6px 18px rgba(0,0,0,.12);border-radius:8px;',
});

/**
 * Single-series area trend with crosshair tooltip.
 * points: [{t: unix seconds, v: number}]
 */
export const MiniTrend = ({ points, height = 120, format = (v) => v, timeFormat = 'HH:mm', name = '' }) => {
    const t = useChartTokens();
    const option = useMemo(() => ({
        animation: false,
        grid: { left: 4, right: 4, top: 8, bottom: 20, containLabel: false },
        tooltip: {
            ...tooltipBase(t),
            trigger: 'axis',
            axisPointer: { type: 'line', lineStyle: { color: t.text3, width: 1 } },
            formatter: (ps) => {
                const p = ps[0];
                return `${dayjs.unix(p.value[0] / 1000).format('MM-DD HH:mm')}<br/>${name ? `${name} ` : ''}<b>${format(p.value[1])}</b>`;
            },
        },
        xAxis: {
            type: 'time',
            axisLine: { lineStyle: { color: t.line } },
            axisTick: { show: false },
            axisLabel: { color: t.text3, fontSize: 10, hideOverlap: true, formatter: (v) => dayjs(v).format(timeFormat) },
            splitLine: { show: false },
        },
        yAxis: { type: 'value', show: false, min: 0 },
        series: [{
            type: 'line', name, showSymbol: false, smooth: 0.25,
            lineStyle: { width: 2, color: t.brand },
            itemStyle: { color: t.brand },
            areaStyle: { color: t.brandSoft },
            data: (points || []).map((p) => [p.t * 1000, p.v]),
        }],
    }), [points, t, format, timeFormat, name]);
    return <ReactECharts option={option} style={{ height, width: '100%' }} notMerge opts={{ renderer: 'svg' }} />;
};

// Approximate country label points (lon, lat) for markers; evm_area.remark = ISO alpha-2 (lower).
const COUNTRY = {
    fr: ['France', 2.3, 46.6], de: ['Germany', 10.4, 51.1], cn: ['China', 104.2, 35.9], ca: ['Canada', -106.3, 56.1],
    us: ['United States', -98.6, 39.8], hk: ['China', 114.17, 22.32], jp: ['Japan', 138.3, 36.2], sg: ['Singapore', 103.8, 1.35],
    gb: ['United Kingdom', -2.5, 54.0], nl: ['Netherlands', 5.3, 52.1], ru: ['Russia', 90, 61], br: ['Brazil', -51.9, -14.2],
    au: ['Australia', 133.8, -25.3], in: ['India', 78.9, 22.6], kr: ['Korea', 127.8, 36.5],
};

/**
 * World map: regions available (light brand) vs regions with the user's instances (brand) + sized markers.
 * regions: [{code, name, available, instances, zones[]}]
 */
export const RegionMap = ({ regions, height = 260 }) => {
    const { t } = useTranslation();
    const tokens = useChartTokens();
    const option = useMemo(() => {
        const byName = {};
        const markers = [];
        (regions || []).forEach((r) => {
            const c = COUNTRY[r.code];
            if (!c) return;
            const [geoName, lon, lat] = c;
            const prev = byName[geoName] || { name: geoName, instances: 0, available: false, labels: [] };
            prev.instances += r.instances || 0;
            prev.available = prev.available || r.available;
            prev.labels.push(r.name);
            byName[geoName] = prev;
            if (r.available || r.instances) {
                markers.push({
                    name: r.name, code: r.code, value: [lon, lat, r.instances || 0], instances: r.instances || 0, zones: r.zones || [], available: r.available,
                    itemStyle: r.instances
                        ? { color: tokens.brand, borderColor: tokens.surface, borderWidth: 2 }
                        : { color: tokens.surface, borderColor: tokens.brand, borderWidth: 2 },
                });
            }
        });
        const regionsStyle = Object.values(byName).map((d) => ({
            name: d.name,
            itemStyle: { areaColor: d.instances > 0 ? tokens.brand : (d.available ? tokens.avail : tokens.land), borderColor: tokens.surface, borderWidth: 0.6 },
            emphasis: { itemStyle: { areaColor: d.instances > 0 ? tokens.brand : tokens.avail } },
        }));
        return {
            animation: false,
            tooltip: {
                ...tooltipBase(tokens),
                trigger: 'item',
                formatter: (p) => {
                    if (p.seriesType !== 'scatter') return '';
                    const d = p.data;
                    const fc = normalizeCountryCode(d.code);
                    const fl = fc ? `<img src="${process.env.PUBLIC_URL || ''}/flags/${fc}.svg" width="18" height="12" alt="" aria-hidden="true" style="vertical-align:-1px;margin-right:6px;border-radius:2px;object-fit:cover;box-shadow:0 0 0 1px rgba(128,128,128,.25)"/>` : '';
                    return `${fl}<b>${d.name}</b><br/>${t('My instances {{instances}}', { instances: d.instances })}<br/>${d.available ? t('Available for activation') : t('Currently unavailable for activation')}${d.zones.length ? `<br/><span style="opacity:.7">${d.zones.join('、')}</span>` : ''}`;
                },
            },
            geo: {
                map: 'world', roam: false, silent: true,
                left: 0, right: 0, top: 0, bottom: 0,
                boundingCoords: [[-170, 78], [180, -58]],
                itemStyle: { areaColor: tokens.land, borderColor: tokens.surface, borderWidth: 0.6 },
                emphasis: { disabled: true },
                regions: regionsStyle,
                label: { show: false },
            },
            series: [{
                type: 'scatter', coordinateSystem: 'geo', geoIndex: 0, zlevel: 2,
                symbolSize: (v) => (v[2] > 0 ? Math.min(10 + v[2] * 3, 24) : 9),
                emphasis: { scale: 1.2 },
                data: markers,
            }],
        };
    }, [regions, tokens, t]);
    return <ReactECharts option={option} style={{ height, width: '100%' }} notMerge />;
};
